import { useEffect, useMemo, useRef, useState } from "react";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import {
  Badge,
  Collapse,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import DropDown from "@/components/forms/inputs/dropDown";
import RoundedContentBox from "@/components/general/roundedContentBox";
import LmsCourseCard, {
  LmsCourseCardData,
} from "@/components/lms/lmsCourseCard";
import HighFiveIcon from "@/icons/highFiveIcon";
import { RootState } from "@/components/state/store";
import {
  downloadLmsCertificate,
  getEffectiveLmsMode,
  getLmsCourseLanguageOptions,
  getLmsCourseOverviewRoute,
  getLmsLearnerEnrolmentOverviewRoute,
  getLmsCoursesRoute,
  LmsMode,
  shouldBlockLmsForSubdomain,
} from "@/components/lms/lms.utils";
import {
  getServerSideCourseNameSearchParams,
  LearnerCourseTab,
  loadPaginatedEntity,
  mapLearnerEnrolmentToCourseCardData,
  getLearnerProgressStatusKey,
} from "@/components/lms/lmsCoursesShared";
import { useLmsCourseListControls } from "@/components/lms/useLmsCourseListControls";
import styles from "@/routes/lms/lmsLearnerCourses.module.css";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";

interface Props {
  requiredMode?: LmsMode;
}

interface FilterOption {
  value: string;
  label: string;
}

interface LearnerFilters {
  status: FilterOption | null;
  language: FilterOption | null;
}

const initialLearnerFilters: LearnerFilters = {
  status: null,
  language: null,
};

export default function LmsLearnerCourses({ requiredMode = "learner" }: Props) {
  const { t } = useTranslation();
  usePageHeading(t("lms_product_name"));
  const navigate = useNavigate();
  const loginState = useSelector((state: RootState) => state.login);
  const {
    activeTab: learnerTab,
    appliedSearchTerm,
    filters: learnerFilters,
    isFiltersOpen,
    searchInput,
    setActiveTab: setLearnerTab,
    setFilters: setLearnerFilters,
    setIsFiltersOpen,
    setSearchInput,
  } = useLmsCourseListControls<LearnerCourseTab, LearnerFilters>({
    initialFilters: initialLearnerFilters,
    initialTab: "active",
  });
  const [activeLearnerCourses, setActiveLearnerCourses] = useState<
    LmsCourseCardData[]
  >([]);
  const [completedLearnerCourses, setCompletedLearnerCourses] = useState<
    LmsCourseCardData[]
  >([]);
  const [learnerTotalCountByTab, setLearnerTotalCountByTab] = useState<
    Record<LearnerCourseTab, number>
  >({
    active: 0,
    completed: 0,
  });
  const [learnerLoadedCount, setLearnerLoadedCount] = useState(0);
  const [inProgressEnrolmentCount, setInProgressEnrolmentCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { handleError, errorModal } = useErrorModal();
  const [completionMessagePhase, setCompletionMessagePhase] = useState<
    | "completeVisible"
    | "completeFadingOut"
    | "celebrationVisible"
    | "celebrationFadingOut"
  >("completeVisible");
  const activeRequestId = useRef(0);

  const subdomain = window.location.hostname.split(".")[0];
  const shouldBlockLms = shouldBlockLmsForSubdomain({
    subdomain,
    permissions: loginState.permissions,
  });
  const effectiveMode = getEffectiveLmsMode({
    subdomain,
    permissions: loginState.permissions,
  });

  const learnerActiveStatusOptions = useMemo(
    () => [
      { value: "not_started", label: t("not_started") },
      { value: "in_progress", label: t("in_progress") },
    ],
    [t]
  );

  const languageOptions = useMemo(() => getLmsCourseLanguageOptions(t), [t]);

  const getLearnerQueryParameters = (tab: LearnerCourseTab = learnerTab) => {
    const parameters: Record<string, string | number> = {
      participant_id: loginState.subjectId,
      prefer_translation: "true",
      ...getServerSideCourseNameSearchParams(
        appliedSearchTerm,
        "course__title__icontains"
      ),
    };

    if (tab === "completed") {
      parameters.status = "completed";
    } else if (learnerFilters.status?.value) {
      parameters.status = learnerFilters.status.value;
    } else {
      parameters.status__in = "not_started,in_progress";
    }

    if (learnerFilters.language?.value) {
      parameters.language = learnerFilters.language.value;
    }

    return parameters;
  };

  const getInactiveLearnerTab = (tab: LearnerCourseTab): LearnerCourseTab =>
    tab === "active" ? "completed" : "active";

  const loadInactiveLearnerTabCount = async (
    activeTab: LearnerCourseTab,
    requestId?: number
  ) => {
    const shouldIgnoreResponse = () =>
      requestId !== undefined && activeRequestId.current !== requestId;

    if (!loginState.subjectId) {
      return 0;
    }

    const inactiveTab = getInactiveLearnerTab(activeTab);

    try {
      const enrolmentResponse = await loadPaginatedEntity(
        "enrolment",
        {
          ...getLearnerQueryParameters(inactiveTab),
          depth: "1",
          limit: 1,
        },
        0
      );

      if (shouldIgnoreResponse()) {
        return null;
      }

      return enrolmentResponse.count;
    } catch (error) {
      if (shouldIgnoreResponse()) {
        return null;
      }
      handleError(error);
      return null;
    }
  };

  const loadInProgressEnrolmentCount = async (requestId?: number) => {
    const shouldIgnoreResponse = () =>
      requestId !== undefined && activeRequestId.current !== requestId;

    if (!loginState.subjectId) {
      return 0;
    }

    try {
      const parameters: Record<string, string | number> = {
        participant_id: loginState.subjectId,
        status: "in_progress",
        limit: 1,
        ...getServerSideCourseNameSearchParams(
          appliedSearchTerm,
          "course__title__icontains"
        ),
      };

      if (learnerFilters.language?.value) {
        parameters.language = learnerFilters.language.value;
      }

      const enrolmentResponse = await loadPaginatedEntity(
        "enrolment",
        parameters,
        0
      );

      if (shouldIgnoreResponse()) {
        return null;
      }

      return enrolmentResponse.count;
    } catch (error) {
      if (shouldIgnoreResponse()) {
        return null;
      }
      handleError(error);
      return null;
    }
  };

  const loadLearnerCoursePage = async (
    offset = 0,
    append = false,
    requestId?: number
  ) => {
    const shouldIgnoreResponse = () =>
      requestId !== undefined && activeRequestId.current !== requestId;

    handleError(null);

    try {
      if (!loginState.subjectId) {
        setActiveLearnerCourses([]);
        setCompletedLearnerCourses([]);
        setLearnerLoadedCount(0);
        return 0;
      }

      const enrolmentResponse = await loadPaginatedEntity(
        "enrolment",
        {
          ...getLearnerQueryParameters(),
          depth: "1",
        },
        offset
      );

      if (shouldIgnoreResponse()) {
        return null;
      }

      const normalizedLearnerCourses = enrolmentResponse.results.map(
        (enrolment) => mapLearnerEnrolmentToCourseCardData(enrolment, t)
      );
      const activeCourses = normalizedLearnerCourses
        .filter((item) => item.tab === "active")
        .map((item) => item.course);
      const completedCourses = normalizedLearnerCourses
        .filter((item) => item.tab === "completed")
        .map((item) => item.course);

      setActiveLearnerCourses((currentCourses) =>
        append ? [...currentCourses, ...activeCourses] : activeCourses
      );
      setCompletedLearnerCourses((currentCourses) =>
        append ? [...currentCourses, ...completedCourses] : completedCourses
      );
      setLearnerLoadedCount((currentLoadedCount) =>
        append
          ? currentLoadedCount + enrolmentResponse.results.length
          : enrolmentResponse.results.length
      );
      return enrolmentResponse.count;
    } catch (error) {
      if (shouldIgnoreResponse()) {
        return null;
      }
      handleError(error);
      return null;
    }
  };

  useEffect(() => {
    const requestId = activeRequestId.current + 1;
    activeRequestId.current = requestId;

    const loadCourses = async () => {
      setIsLoading(true);
      const [learnerTabCount, inactiveTabCount, inProgressCount] =
        await Promise.all([
          loadLearnerCoursePage(0, false, requestId),
          loadInactiveLearnerTabCount(learnerTab, requestId),
          loadInProgressEnrolmentCount(requestId),
        ]);
      if (activeRequestId.current === requestId) {
        if (
          learnerTabCount !== null &&
          inactiveTabCount !== null &&
          inProgressCount !== null
        ) {
          setLearnerTotalCountByTab({
            active:
              learnerTab === "active" ? learnerTabCount : inactiveTabCount,
            completed:
              learnerTab === "completed" ? learnerTabCount : inactiveTabCount,
          });
          setInProgressEnrolmentCount(inProgressCount);
        }
        setIsLoading(false);
      }
    };

    loadCourses();

    return () => {
      activeRequestId.current = requestId;
    };
  }, [loginState.subjectId, learnerTab, learnerFilters, appliedSearchTerm]);

  const visibleLearnerCourses =
    learnerTab === "active" ? activeLearnerCourses : completedLearnerCourses;

  const activeFilterCount = useMemo(
    () =>
      [
        learnerTab === "active" ? learnerFilters.status : null,
        learnerFilters.language,
      ].filter(Boolean).length,
    [learnerFilters, learnerTab]
  );

  const learnerTotalCount = learnerTotalCountByTab[learnerTab];
  const totalAssignedCourses =
    learnerTotalCountByTab.active + learnerTotalCountByTab.completed;
  const completionProgressPercent =
    totalAssignedCourses > 0
      ? Math.min(
          100,
          Math.round(
            (learnerTotalCountByTab.completed / totalAssignedCourses) * 100
          )
        )
      : 0;
  const remainingCoursesCount = Math.max(
    totalAssignedCourses - learnerTotalCountByTab.completed,
    0
  );
  const hasAssignedCourses = totalAssignedCourses > 0;
  const hasCompletedCourses = learnerTotalCountByTab.completed > 0;
  const isLearnerJourneyCompleted =
    hasAssignedCourses &&
    learnerTotalCountByTab.completed >= totalAssignedCourses;
  const learnerProgressStatusKey = getLearnerProgressStatusKey({
    assignedCount: totalAssignedCourses,
    completedCount: learnerTotalCountByTab.completed,
    inProgressCount: inProgressEnrolmentCount,
  });
  const learnerProgressStatusMessage =
    learnerProgressStatusKey === "learner_progress_complete"
      ? completionMessagePhase === "celebrationVisible" ||
        completionMessagePhase === "celebrationFadingOut"
        ? t("learner_progress_celebration")
        : t("learner_progress_complete")
      : learnerProgressStatusKey === "learner_progress_status_not_started"
      ? t("learner_progress_status_not_started", {
          count: remainingCoursesCount,
        })
      : t(learnerProgressStatusKey);
  const hasMoreCourses = learnerLoadedCount < learnerTotalCount;
  const hasActiveListFilters =
    activeFilterCount > 0 || Boolean(appliedSearchTerm);
  const emptyStateCopy = useMemo(() => {
    if (hasActiveListFilters) {
      if (appliedSearchTerm) {
        return {
          title: t("no_results_found"),
          message: t("no_courses_found_for_search_term", {
            searchTerm: appliedSearchTerm,
          }),
          isNoResults: true,
        };
      }

      return {
        title: t("no_results_found"),
        message: t("no_results_for_selected_filter"),
        isNoResults: true,
      };
    }

    if (learnerTab === "completed") {
      if (!hasAssignedCourses) {
        return {
          title: t("completed_courses_empty_title"),
          message: t("completed_courses_empty_message_no_assigned"),
          isNoResults: false,
        };
      }

      if (!hasCompletedCourses) {
        return {
          title: t("completed_courses_empty_title"),
          message: t("completed_courses_empty_message_with_assigned"),
          isNoResults: false,
        };
      }

      return {
        title: t("completed_courses_empty_title"),
        message: t("completed_courses_empty_message_generic"),
        isNoResults: false,
      };
    }

    if (!hasAssignedCourses) {
      return {
        title: t("no_courses_yet_title"),
        message: t("no_courses_yet_message"),
        isNoResults: false,
      };
    }

    return {
      title: t("active_courses_empty_title"),
      message: t("active_courses_empty_message"),
      isNoResults: false,
    };
  }, [
    hasActiveListFilters,
    hasAssignedCourses,
    hasCompletedCourses,
    learnerTab,
    appliedSearchTerm,
    t,
  ]);

  useEffect(() => {
    if (!isLearnerJourneyCompleted) {
      setCompletionMessagePhase("completeVisible");
      return;
    }

    const timeoutIds: ReturnType<typeof setTimeout>[] = [];

    const startCycle = () => {
      setCompletionMessagePhase("completeVisible");

      timeoutIds.push(
        setTimeout(() => {
          setCompletionMessagePhase("completeFadingOut");
        }, 5000)
      );

      timeoutIds.push(
        setTimeout(() => {
          setCompletionMessagePhase("celebrationVisible");
        }, 8000)
      );

      timeoutIds.push(
        setTimeout(() => {
          setCompletionMessagePhase("celebrationFadingOut");
        }, 13000)
      );

      timeoutIds.push(
        setTimeout(() => {
          startCycle();
        }, 16000)
      );
    };

    startCycle();

    return () => {
      timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, [isLearnerJourneyCompleted]);

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMoreCourses) {
      return;
    }

    setIsLoadingMore(true);
    const nextLearnerTotalCount = await loadLearnerCoursePage(
      learnerLoadedCount,
      true
    );
    if (nextLearnerTotalCount !== null) {
      setLearnerTotalCountByTab((currentCount) => ({
        ...currentCount,
        [learnerTab]: nextLearnerTotalCount,
      }));
    }
    setIsLoadingMore(false);
  };

  const handleLearnerFilterChange = (
    filterKey: keyof LearnerFilters,
    option: FilterOption | null
  ) => {
    setLearnerFilters((currentFilters) => ({
      ...currentFilters,
      [filterKey]: option,
    }));
  };

  const handleResetFilters = () => {
    setLearnerFilters(initialLearnerFilters);
  };

  const handleOpenCourseOverview = (course: LmsCourseCardData) => {
    if (course.enrolmentId) {
      navigate(
        getLmsLearnerEnrolmentOverviewRoute(course.enrolmentId, course.language)
      );
      return;
    }

    const courseId = course.courseId ?? course.id;
    navigate(getLmsCourseOverviewRoute(effectiveMode, courseId));
  };

  const handleDownloadCertificate = async (enrolmentId: string) => {
    try {
      await downloadLmsCertificate({ enrolmentId });
    } catch (message) {
      handleError(message);
    }
  };

  if (shouldBlockLms) {
    return <Navigate to="/" replace />;
  }

  if (requiredMode && effectiveMode !== requiredMode) {
    return <Navigate to={getLmsCoursesRoute(effectiveMode)} replace />;
  }

  return (
    <>
      <Grid item xs={12} className={styles.summaryCardsContainer}>
        <RoundedContentBox className={styles.learningProgressHero}>
          <div className={styles.heroHeader}>
            <h2 className={styles.heroTitle}>
              {t("learner_progress_title")}
            </h2>
          </div>

          <div className={styles.heroProgressLabels}>
            <p className={styles.heroProgressText}>
              {isLoading
                ? t("loading")
                : t("learner_progress_completed", {
                    completed: learnerTotalCountByTab.completed,
                    total: totalAssignedCourses,
                  })}
            </p>
            <p
              className={`${styles.heroStatusMessage} ${
                !isLoading && isLearnerJourneyCompleted
                  ? `${styles.heroCompleteText} ${
                      completionMessagePhase === "completeFadingOut" ||
                      completionMessagePhase === "celebrationFadingOut"
                        ? styles.heroStatusFadeOut
                        : ""
                    } ${
                      completionMessagePhase === "celebrationVisible" ||
                      completionMessagePhase === "celebrationFadingOut"
                        ? styles.heroCelebrationText
                        : ""
                    }`
                  : styles.heroRemainingText
              }`}
            >
              {isLoading ? "" : learnerProgressStatusMessage}
            </p>
          </div>

          <div className={styles.progressTrack} aria-busy={isLoading}>
            <div
              className={styles.progressFill}
              data-testid="lms-learner-progress-fill"
              style={{
                width: `${isLoading ? 0 : completionProgressPercent}%`,
              }}
            />
          </div>
        </RoundedContentBox>
      </Grid>
      <Grid item xs={12} className={styles.learnerTabsContainer}>
        <RoundedContentBox>
          <Tabs
            value={learnerTab}
            onChange={(_, newValue) => setLearnerTab(newValue)}
            className={styles.learnerTabs}
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: "#8c1ec8",
              },
              "& .MuiTab-root.Mui-selected": {
                color: "#8c1ec8",
              },
            }}
          >
            <Tab
              data-testid="lms-learner-courses-active-tab"
              value="active"
              label={
                <span className={styles.tabLabelWithCount}>
                  {t("open_courses")}
                  <span className={styles.tabCount}>
                    {learnerTotalCountByTab.active}
                  </span>
                </span>
              }
              className={styles.learnerTabActive}
            />
            <Tab
              data-testid="lms-learner-courses-completed-tab"
              value="completed"
              label={
                <span className={styles.tabLabelWithCount}>
                  {t("completed_courses")}
                  <span className={styles.tabCount}>
                    {learnerTotalCountByTab.completed}
                  </span>
                </span>
              }
              className={styles.learnerTabCompleted}
            />
          </Tabs>
          <>
            <div className={styles.searchFilterContainer}>
              <div
                className={styles.searchFilterInput}
                data-testid="lms-learner-course-search"
              >
                <DefaultInput
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder={t("course_title")}
                  style={{ width: "100%" }}
                  inputStyle={{
                    boxSizing: "border-box",
                    height: "56px",
                    paddingLeft: "2.5rem",
                    paddingRight: searchInput ? "2.5rem" : "0.75rem",
                    width: "100%",
                  }}
                />
                <SearchOutlinedIcon className={styles.searchStartIcon} />
                {searchInput && (
                  <ClearIcon
                    className={styles.searchClearIcon}
                    onClick={() => setSearchInput("")}
                  />
                )}
              </div>
              <Badge
                badgeContent={activeFilterCount}
                color="secondary"
                invisible={activeFilterCount === 0}
                overlap="rectangular"
              >
                <GenericButton
                  variant="outlined"
                  color="tertiary"
                  onClick={() =>
                    setIsFiltersOpen((currentState) => !currentState)
                  }
                  startIcon={<TuneOutlinedIcon />}
                >
                  {t("more_filters")}
                </GenericButton>
              </Badge>
            </div>
            <Collapse in={isFiltersOpen} timeout={250}>
              <div className={styles.filtersCollapseContainer}>
                <div
                  className={
                    learnerTab === "active"
                      ? styles.filtersGridLearner
                      : styles.filtersGridLearnerSingle
                  }
                >
                  {learnerTab === "active" && (
                    <DropDown
                      options={learnerActiveStatusOptions}
                      onChange={(option: any) =>
                        handleLearnerFilterChange("status", option)
                      }
                      externalValue={learnerFilters.status}
                      isClearable={true}
                      placeholder={t("completion_state")}
                      name="learner-status"
                    />
                  )}
                  <DropDown
                    options={languageOptions}
                    onChange={(option: any) =>
                      handleLearnerFilterChange("language", option)
                    }
                    externalValue={learnerFilters.language}
                    isClearable={true}
                    placeholder={t("language")}
                    name="learner-language"
                  />
                </div>
                <div className={styles.resetFiltersContainer}>
                  <GenericButton
                    onClick={handleResetFilters}
                    startIcon={<ClearIcon />}
                    disabled={activeFilterCount === 0}
                  >
                    {t("reset_filters")}
                  </GenericButton>
                </div>
              </div>
            </Collapse>
          </>
        </RoundedContentBox>
      </Grid>

      <Grid item xs={12}>
        {isLoading ? (
          <RoundedContentBox className={styles.loadingContainer}>
            <CircularProgress color="secondary" />
          </RoundedContentBox>
        ) : visibleLearnerCourses.length === 0 ? (
          <RoundedContentBox className={styles.emptyCoursesContainer}>
            {emptyStateCopy.isNoResults || learnerTab === "completed" ? (
              <>
                <h3 className={styles.emptyCoursesTitle}>
                  {emptyStateCopy.title}
                </h3>
                <HeartBrokenIcon
                  color="secondary"
                  className={styles.noResultsIcon}
                  data-testid="lms-learner-no-results-icon"
                />
                {emptyStateCopy.message && (
                  <p className={styles.emptyCoursesMessage}>
                    {emptyStateCopy.message}
                  </p>
                )}
              </>
            ) : (
              <>
                <HighFiveIcon width="160" height="159" />
                <h3 className={styles.emptyCoursesTitle}>
                  {emptyStateCopy.title}
                </h3>
                {emptyStateCopy.message && (
                  <p className={styles.emptyCoursesMessage}>
                    {emptyStateCopy.message}
                  </p>
                )}
              </>
            )}
          </RoundedContentBox>
        ) : (
          <>
            <Grid container spacing={"1rem"}>
              {visibleLearnerCourses.map((course) => (
                <Grid item xs={12} md={6} lg={4} key={course.id}>
                  <LmsCourseCard
                    course={course}
                    onClick={() => handleOpenCourseOverview(course)}
                    onDownloadCertificate={handleDownloadCertificate}
                  />
                </Grid>
              ))}
            </Grid>
            <div className={styles.loadMoreContainer}>
              <span className={styles.loadMoreText}>
                {t("showing_courses", {
                  shown: learnerLoadedCount,
                  total: learnerTotalCount,
                })}
              </span>
              {hasMoreCourses && (
                <>
                  <span className={styles.loadMoreSeparator}>|</span>
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    className={styles.loadMoreButton}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? t("loading") : t("load_more")}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </Grid>

      {errorModal}
    </>
  );
}
